import React, { createContext, useContext, useState, useCallback } from 'react';
import type {
  BuilderBlock,
  TriggerType,
  MissionType,
  StepType,
  TargetType,
  MissionTemplate,
} from './types';

// ─── Block Definitions (palette source) ─────────────────────────────────────

export const TRIGGER_BLOCKS: Omit<BuilderBlock, 'id' | 'config'>[] = [
  { category: 'trigger', type: 'idle_time', label: 'Idle Time', icon: 'HourglassEmpty', color: '#7c3aed' },
  { category: 'trigger', type: 'interval', label: 'Interval', icon: 'Timer', color: '#7c3aed' },
  { category: 'trigger', type: 'time_of_day', label: 'Time of Day', icon: 'Schedule', color: '#7c3aed' },
  { category: 'trigger', type: 'manual', label: 'Manual', icon: 'TouchApp', color: '#7c3aed' },
];

export const MISSION_BLOCKS: Omit<BuilderBlock, 'id' | 'config'>[] = [
  { category: 'mission', type: 'stock_take', label: 'Stock Take', icon: 'Inventory', color: '#0891b2' },
  { category: 'mission', type: 'verify_product', label: 'Verify Product', icon: 'FactCheck', color: '#0891b2' },
];

export const STEP_BLOCKS: Omit<BuilderBlock, 'id' | 'config'>[] = [
  { category: 'step', type: 'scan_barcode', label: 'Scan Barcode', icon: 'QrCodeScanner', color: '#059669' },
  { category: 'step', type: 'take_photo', label: 'Take Photo', icon: 'CameraAlt', color: '#059669' },
  { category: 'step', type: 'enter_description', label: 'Enter Description', icon: 'Description', color: '#059669' },
  { category: 'step', type: 'search_product', label: 'Search Product', icon: 'Search', color: '#059669' },
  { category: 'step', type: 'count_qty', label: 'Count Quantity', icon: 'Numbers', color: '#059669' },
];

let blockIdCounter = 0;
export function createBlock(template: Omit<BuilderBlock, 'id' | 'config'>): BuilderBlock {
  return { ...template, id: `block-${++blockIdCounter}`, config: {} };
}

// ─── Context ────────────────────────────────────────────────────────────────

interface MissionBuilderState {
  // View
  view: 'list' | 'builder' | 'instances';
  setView: (v: 'list' | 'builder' | 'instances') => void;

  // Builder state
  blocks: BuilderBlock[];
  selectedBlockId: string | null;
  missionName: string;
  missionDescription: string;
  storeId: string;
  assignToRole: string;

  // Editing existing template
  editingTemplate: MissionTemplate | null;

  // Actions
  addBlock: (block: BuilderBlock) => void;
  removeBlock: (id: string) => void;
  reorderBlocks: (fromIndex: number, toIndex: number) => void;
  selectBlock: (id: string | null) => void;
  updateBlockConfig: (id: string, config: Record<string, any>) => void;
  setMissionName: (name: string) => void;
  setMissionDescription: (desc: string) => void;
  setStoreId: (id: string) => void;
  setAssignToRole: (role: string) => void;
  resetBuilder: () => void;
  loadTemplate: (template: MissionTemplate) => void;
  selectedTemplate: MissionTemplate | null;
  setSelectedTemplate: (t: MissionTemplate | null) => void;

  // Serialization
  serialize: () => {
    name: string;
    description: string;
    storeId: string;
    missionType: MissionType;
    triggerType: TriggerType;
    triggerConfig: Record<string, any>;
    targetType: TargetType;
    targetConfig: Record<string, any>;
    steps: { type: StepType; required: boolean; order: number; instructions?: string }[];
    assignToRole: string;
  } | null;
}

const MissionBuilderContext = createContext<MissionBuilderState | null>(null);

export function useMissionBuilder() {
  const ctx = useContext(MissionBuilderContext);
  if (!ctx) throw new Error('useMissionBuilder must be used within MissionBuilderProvider');
  return ctx;
}

export function MissionBuilderProvider({ children }: { children: React.ReactNode }) {
  const [view, setView] = useState<'list' | 'builder' | 'instances'>('list');
  const [blocks, setBlocks] = useState<BuilderBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [missionName, setMissionName] = useState('');
  const [missionDescription, setMissionDescription] = useState('');
  const [storeId, setStoreId] = useState('');
  const [assignToRole, setAssignToRole] = useState('picker');
  const [editingTemplate, setEditingTemplate] = useState<MissionTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MissionTemplate | null>(null);

  const addBlock = useCallback((block: BuilderBlock) => {
    setBlocks((prev) => {
      // Only one trigger and one mission block allowed
      if (block.category === 'trigger') {
        const filtered = prev.filter((b) => b.category !== 'trigger');
        return [block, ...filtered];
      }
      if (block.category === 'mission') {
        const triggers = prev.filter((b) => b.category === 'trigger');
        const steps = prev.filter((b) => b.category === 'step');
        return [...triggers, block, ...steps.filter((b) => b.category !== 'mission' as any)];
      }
      return [...prev, block];
    });
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setSelectedBlockId((prev) => (prev === id ? null : prev));
  }, []);

  const reorderBlocks = useCallback((fromIndex: number, toIndex: number) => {
    setBlocks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const updateBlockConfig = useCallback((id: string, config: Record<string, any>) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, config: { ...b.config, ...config } } : b)));
  }, []);

  const resetBuilder = useCallback(() => {
    setBlocks([]);
    setSelectedBlockId(null);
    setMissionName('');
    setMissionDescription('');
    setStoreId('');
    setAssignToRole('picker');
    setEditingTemplate(null);
  }, []);

  const loadTemplate = useCallback((template: MissionTemplate) => {
    setEditingTemplate(template);
    setMissionName(template.name);
    setMissionDescription(template.description || '');
    setStoreId(template.storeId || '');
    setAssignToRole(template.assignToRole);

    const newBlocks: BuilderBlock[] = [];

    // Trigger block
    const triggerDef = TRIGGER_BLOCKS.find((b) => b.type === template.triggerType);
    if (triggerDef) {
      newBlocks.push({ ...triggerDef, id: `block-${++blockIdCounter}`, config: template.triggerConfig || {} });
    }

    // Mission type block
    const missionDef = MISSION_BLOCKS.find((b) => b.type === template.missionType);
    if (missionDef) {
      newBlocks.push({
        ...missionDef,
        id: `block-${++blockIdCounter}`,
        config: { targetType: template.targetType, ...template.targetConfig },
      });
    }

    // Step blocks
    template.steps.forEach((step) => {
      const stepDef = STEP_BLOCKS.find((b) => b.type === step.type);
      if (stepDef) {
        newBlocks.push({
          ...stepDef,
          id: `block-${++blockIdCounter}`,
          config: { required: step.required, instructions: step.instructions },
        });
      }
    });

    setBlocks(newBlocks);
    setView('builder');
  }, []);

  const serialize = useCallback(() => {
    const trigger = blocks.find((b) => b.category === 'trigger');
    const mission = blocks.find((b) => b.category === 'mission');
    const steps = blocks.filter((b) => b.category === 'step');

    if (!trigger || !mission || steps.length === 0) return null;

    const { targetType: tt, ...targetRest } = mission.config;

    return {
      name: missionName,
      description: missionDescription,
      storeId,
      missionType: mission.type as MissionType,
      triggerType: trigger.type as TriggerType,
      triggerConfig: trigger.config,
      targetType: (tt || 'random') as TargetType,
      targetConfig: targetRest,
      steps: steps.map((s, i) => ({
        type: s.type as StepType,
        required: s.config.required !== false,
        order: i + 1,
        instructions: s.config.instructions,
      })),
      assignToRole,
    };
  }, [blocks, missionName, missionDescription, storeId, assignToRole]);

  return (
    <MissionBuilderContext.Provider
      value={{
        view, setView,
        blocks, selectedBlockId, missionName, missionDescription, storeId, assignToRole,
        editingTemplate, selectedTemplate, setSelectedTemplate,
        addBlock, removeBlock, reorderBlocks,
        selectBlock: setSelectedBlockId, updateBlockConfig,
        setMissionName, setMissionDescription, setStoreId, setAssignToRole,
        resetBuilder, loadTemplate, serialize,
      }}
    >
      {children}
    </MissionBuilderContext.Provider>
  );
}
